import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc,
  deleteDoc,
  doc,
  query,
  where
} from 'firebase/firestore';
// Remove Firebase Storage imports and use local data management
import { db } from '../firebase';
import { 
  Form, 
  Button, 
  Card, 
  Table, 
  Alert,
  Modal
} from 'react-bootstrap';

/**
 * CompanyManager component - For managing companies and their logos
 */
const CompanyManager = () => {
  // State variables
  const [companies, setCompanies] = useState([]);
  const [message, setMessage] = useState({ text: '', type: '' });
  
  // Form state
  const [companyName, setCompanyName] = useState('');
  const [companyCountry, setCompanyCountry] = useState('UK'); // Default to UK
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  
  // Fetch companies from Firestore
  const fetchCompanies = async () => {
    try {
      // First, clear any existing companies to avoid duplicates
      setCompanies([]);
      
      // Get all companies from Firestore
      const companiesRef = collection(db, 'companies');
      const querySnapshot = await getDocs(companiesRef);
      
      // Create a map to deduplicate companies by name
      const companyMap = {};
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Only add if we haven't seen this company name before
        if (!companyMap[data.name]) {
          companyMap[data.name] = { id: doc.id, ...data };
        }
      });
      
      // Convert map to array
      const companiesData = Object.values(companyMap);
      
      setCompanies(companiesData);
      console.log('Fetched companies:', companiesData.length);
    } catch (error) {
      console.error("Error fetching companies:", error);
      setMessage({ text: 'Error fetching companies: ' + error.message, type: 'danger' });
    }
  };
  
  // Handle logo file selection
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      
      // Create a preview URL
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
    }
  };
  
  // Import existing companies from shift patterns
  const importExistingCompanies = async () => {
    try {
      // First, get all existing companies from the companies collection
      const companiesRef = collection(db, 'companies');
      const companiesSnapshot = await getDocs(companiesRef);
      
      // Create a map of company names to their document IDs
      const existingCompanyMap = {};
      companiesSnapshot.forEach((doc) => {
        const data = doc.data();
        existingCompanyMap[data.name] = doc.id;
      });
      
      // Get all unique company names from shift patterns
      const shiftsRef = collection(db, 'shiftPatterns');
      const shiftsSnapshot = await getDocs(shiftsRef);
      
      const uniqueCompanies = new Set();
      shiftsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.company) {
          uniqueCompanies.add(data.company);
        }
      });
      
      // Add companies that exist in patterns but not in companies collection
      const companiesToAdd = [];
      for (const companyName of uniqueCompanies) {
        if (!existingCompanyMap[companyName]) {
          // Company doesn't exist in companies collection, add it
          await addDoc(collection(db, 'companies'), {
            name: companyName,
            country: 'UK', // Default to UK for imported companies
            logoUrl: '',
            updatedAt: new Date().toISOString()
          });
          companiesToAdd.push(companyName);
        }
      }
      
      if (companiesToAdd.length > 0) {
        fetchCompanies(); // Refresh the list
        setMessage({ 
          text: `Imported ${companiesToAdd.length} existing companies from shift patterns`, 
          type: 'success' 
        });
      }
    } catch (error) {
      console.error('Error importing existing companies:', error);
      setMessage({ 
        text: 'Error importing existing companies: ' + error.message, 
        type: 'warning' 
      });
    }
  };
  
  // Clean up duplicate companies in Firestore
  const cleanupDuplicateCompanies = async () => {
    try {
      setMessage({ text: 'Cleaning up duplicate companies...', type: 'info' });
      
      // Get all companies from Firestore
      const companiesRef = collection(db, 'companies');
      const querySnapshot = await getDocs(companiesRef);
      
      // Create a map to track companies by name
      const companyMap = {};
      const duplicatesToDelete = [];
      
      // First pass: identify duplicates
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const companyName = data.name;
        
        if (!companyMap[companyName]) {
          // First occurrence of this company name
          companyMap[companyName] = {
            id: doc.id,
            logoUrl: data.logoUrl || '',
            updatedAt: data.updatedAt || new Date().toISOString()
          };
        } else {
          // This is a duplicate
          // If this duplicate has a logo and the original doesn't, keep the logo
          if (data.logoUrl && !companyMap[companyName].logoUrl) {
            companyMap[companyName].logoUrl = data.logoUrl;
          }
          
          // Mark this duplicate for deletion
          duplicatesToDelete.push(doc.id);
        }
      });
      
      // Second pass: update the original companies with any logos from duplicates
      for (const companyName in companyMap) {
        const company = companyMap[companyName];
        if (company.logoUrl) {
          // Update the company document in Firestore
          const companyRef = doc(db, 'companies', company.id);
          await updateDoc(companyRef, {
            name: companyName,
            country: company.country || 'UK', // Add country field
            logoUrl: company.logoUrl,
            updatedAt: new Date().toISOString()
          });
        }
      }
      
      // Third pass: delete duplicates
      for (const docId of duplicatesToDelete) {
        await deleteDoc(doc(db, 'companies', docId));
      }
      
      if (duplicatesToDelete.length > 0) {
        setMessage({ 
          text: `Cleaned up ${duplicatesToDelete.length} duplicate companies`, 
          type: 'success' 
        });
        // Refresh the companies list
        fetchCompanies();
      } else {
        setMessage({ text: 'No duplicate companies found', type: 'info' });
      }
    } catch (error) {
      console.error('Error cleaning up duplicate companies:', error);
      setMessage({ 
        text: 'Error cleaning up duplicate companies: ' + error.message, 
        type: 'danger' 
      });
    }
  };
  
  // Fetch companies on component mount
  useEffect(() => {
    // Define a function that will run both operations
    const initializeCompanies = async () => {
      await importExistingCompanies();
      await cleanupDuplicateCompanies(); // Add cleanup step
      fetchCompanies();
    };
    
    // Run the initialization
    initializeCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // We only want this to run once on mount
  
  // Convert file to data URL for local storage
  const getDataUrl = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Make sure we have a valid data URL
        const result = reader.result;
        if (result && result.startsWith('data:')) {
          console.log('Successfully converted file to data URL');
          resolve(result);
        } else {
          reject(new Error('Invalid data URL format'));
        }
      };
      reader.onerror = error => reject(error);
    });
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!companyName.trim()) {
      setMessage({ text: 'Company name is required', type: 'warning' });
      return;
    }
    
    try {
      setIsUploading(true);
      
      // Convert logo file to data URL if selected
      let logoUrl = editMode && !logoFile ? companies.find(c => c.id === editId)?.logoUrl || null : null;
      
      if (logoFile) {
        try {
          logoUrl = await getDataUrl(logoFile);
          console.log('Logo uploaded successfully, data URL length:', logoUrl.length);
          
          // Validate the data URL
          if (!logoUrl.startsWith('data:image/')) {
            console.error('Invalid image data URL format');
            setMessage({ text: 'Error processing logo: Invalid format', type: 'danger' });
            setIsUploading(false);
            return;
          }
        } catch (error) {
          console.error('Error processing logo:', error);
          setMessage({ text: 'Error processing logo: ' + error.message, type: 'danger' });
          setIsUploading(false);
          return;
        }
      }
      
      const companyData = {
        name: companyName.trim(),
        country: companyCountry, // Add country field
        updatedAt: new Date().toISOString()
      };
      
      // Only add logoUrl if we have one
      if (logoUrl) {
        companyData.logoUrl = logoUrl;
      }
      
      if (editMode && editId) {
        // Get the original company name before updating
        const originalCompany = companies.find(c => c.id === editId);
        const originalCompanyName = originalCompany ? originalCompany.name : null;
        
        // Update existing company
        await updateDoc(doc(db, 'companies', editId), companyData);
        
        // If company name has changed, update all references in shift patterns and friends
        if (originalCompanyName && originalCompanyName !== companyName.trim()) {
          let totalUpdates = 0;
          
          // 1. Update shift patterns
          const shiftsRef = collection(db, 'shiftPatterns');
          const shiftsQuery = query(shiftsRef, where('company', '==', originalCompanyName));
          const shiftsSnapshot = await getDocs(shiftsQuery);
          
          const shiftUpdatePromises = [];
          shiftsSnapshot.forEach((docSnapshot) => {
            shiftUpdatePromises.push(
              updateDoc(docSnapshot.ref, { company: companyName.trim() })
            );
          });
          
          if (shiftUpdatePromises.length > 0) {
            await Promise.all(shiftUpdatePromises);
            totalUpdates += shiftUpdatePromises.length;
          }
          
          // 2. Update friends for all users
          // First get all users
          const usersRef = collection(db, 'users');
          const usersSnapshot = await getDocs(usersRef);
          
          const friendUpdatePromises = [];
          
          // For each user, check their friends collection
          for (const userDoc of usersSnapshot.docs) {
            const userId = userDoc.id;
            const friendsRef = collection(db, 'users', userId, 'friends');
            const friendsQuery = query(friendsRef, where('company', '==', originalCompanyName));
            const friendsSnapshot = await getDocs(friendsQuery);
            
            // Update each friend with the new company name
            friendsSnapshot.forEach((friendDoc) => {
              // Skip placeholder documents
              if (!friendDoc.data().placeholder) {
                friendUpdatePromises.push(
                  updateDoc(friendDoc.ref, { company: companyName.trim() })
                );
              }
            });
          }
          
          if (friendUpdatePromises.length > 0) {
            await Promise.all(friendUpdatePromises);
            totalUpdates += friendUpdatePromises.length;
          }
          
          // Show success message with total updates
          setMessage({ 
            text: `Company updated successfully! Updated ${totalUpdates} references (${shiftUpdatePromises.length} shift patterns, ${friendUpdatePromises.length} friends).`, 
            type: 'success' 
          });
        } else {
          setMessage({ text: 'Company updated successfully!', type: 'success' });
        }
      } else {
        // Check if company with same name already exists
        const companiesRef = collection(db, 'companies');
        const q = query(companiesRef, where('name', '==', companyName.trim()));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          setMessage({ text: 'A company with this name already exists', type: 'danger' });
          setIsUploading(false);
          return;
        }
        
        // Create company document in Firestore
        const companyData = {
          name: companyName,
          country: companyCountry, // Add country field
          logoUrl: '', // Initialize with empty string
          updatedAt: new Date().toISOString()
        };
        
        // Only add logoUrl if we have one
        if (logoUrl) {
          companyData.logoUrl = logoUrl;
        }
        
        const docRef = await addDoc(collection(db, 'companies'), companyData);
        console.log('Company added with ID:', docRef.id);
        setMessage({ text: 'Company added successfully!', type: 'success' });
      }
      
      // Reset form
      setCompanyName('');
      setLogoFile(null);
      setLogoPreview(null);
      setEditMode(false);
      setEditId(null);
      setShowModal(false);
      
      // Refresh companies list
      fetchCompanies();
    } catch (error) {
      console.error("Error saving company:", error);
      setMessage({ text: 'Error saving company: ' + error.message, type: 'danger' });
    } finally {
      setIsUploading(false);
    }
  };
  
  // Handle edit company
  const handleEdit = (company) => {
    setEditMode(true);
    setEditId(company.id);
    setCompanyName(company.name);
    setCompanyCountry(company.country || 'UK'); // Default to UK if no country is set
    
    // If there's a logo URL, set it as the preview
    if (company.logoUrl) {
      setLogoPreview(company.logoUrl);
    } else {
      setLogoPreview(null);
    }
    
    // Show the modal
    setShowModal(true);
  };
  
  // Handle delete company
  const handleDelete = async (companyId, logoUrl) => {
    if (window.confirm('Are you sure you want to delete this company? This will not affect existing shift patterns.')) {
      try {
        // Delete from Firestore
        await deleteDoc(doc(db, 'companies', companyId));
        
        // No need to delete logo from storage since we're using data URLs
        
        setMessage({ text: 'Company deleted successfully!', type: 'success' });
        
        // Refresh companies list
        fetchCompanies();
      } catch (error) {
        console.error("Error deleting company:", error);
        setMessage({ text: 'Error deleting company: ' + error.message, type: 'danger' });
      }
    }
  };
  
  // Handle modal close
  const handleCloseModal = () => {
    // Reset form state
    setEditMode(false);
    setEditId(null);
    setCompanyName('');
    setCompanyCountry('UK'); // Reset to default
    setLogoFile(null);
    setLogoPreview(null);
    setShowModal(false);
  };
  
  return (
    <>
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Companies</h5>
          <Button 
            variant="primary" 
            size="sm"
            onClick={() => setShowModal(true)}
          >
            Add Company
          </Button>
        </Card.Header>
        <Card.Body>
          {message.text && (
            <Alert variant={message.type} onClose={() => setMessage({ text: '', type: '' })} dismissible>
              {message.text}
            </Alert>
          )}
          
          {companies.length > 0 ? (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Country</th>
                  <th>Logo</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => (
                  <tr key={company.id}>
                    <td>{company.name}</td>
                    <td>{company.country || 'UK'}</td>
                    <td>
                      {company.logoUrl ? (
                        <img 
                          src={company.logoUrl} 
                          alt={`${company.name} logo`} 
                          style={{ maxWidth: '100px', maxHeight: '40px' }} 
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      ) : (
                        <span className="text-muted">No logo</span>
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button 
                          variant="primary" 
                          size="sm"
                          onClick={() => handleEdit(company)}
                        >
                          Edit
                        </Button>
                        <Button 
                          variant="danger" 
                          size="sm"
                          onClick={() => handleDelete(company.id, company.logoUrl)}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <Alert variant="info">
              No companies found. Add some using the "Add Company" button.
            </Alert>
          )}
        </Card.Body>
      </Card>
      
      {/* Add/Edit Company Modal */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{editMode ? 'Edit Company' : 'Add Company'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Company Name</Form.Label>
              <Form.Control
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
                placeholder="Enter company name"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Company Location</Form.Label>
              <Form.Select
                value={companyCountry}
                onChange={(e) => setCompanyCountry(e.target.value)}
                required
              >
                <option value="UK">United Kingdom</option>
                <option value="Ireland">Ireland</option>
              </Form.Select>
              <Form.Text className="text-muted">
                This will determine which bank holidays are displayed for this company
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Company Logo</Form.Label>
              <div className="d-flex flex-column">
                {logoPreview && (
                  <div className="mb-2">
                    <img 
                      src={logoPreview} 
                      alt="Logo Preview" 
                      style={{ maxWidth: '100%', maxHeight: '100px' }} 
                      className="border rounded p-1"
                    />
                  </div>
                )}
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  disabled={isUploading}
                />
                <Form.Text className="text-muted">
                  Upload a company logo image (PNG, JPG, GIF)
                </Form.Text>
                {isUploading && (
                  <div className="mt-2">
                    <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <span>Uploading logo...</span>
                  </div>
                )}
              </div>
            </Form.Group>
            
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={isUploading}>
                {editMode ? 'Update Company' : 'Add Company'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CompanyManager;
